/**
 * Lógica de acomodo manual de carga en un contenedor.
 *
 * Complementa a `bin-packing.ts`: ese resuelve el acomodo automático, este
 * habilita el manual — arrastrar, rotar y apilar como en un Tetris, validando
 * cada movimiento contra las paredes, las demás cajas y el peso.
 *
 * Sistema de coordenadas (el mismo que usa el packing, en centímetros):
 *
 *        z (alto)
 *        │
 *        │   ╱ y (profundidad / ancho del contenedor)
 *        │  ╱
 *        └─────── x (largo del contenedor)
 *
 * El origen (0,0,0) es la esquina inferior interna del fondo izquierdo.
 * `container-box.tsx` traduce a three.js, donde el alto es el eje Y.
 *
 * Todo acá es puro: sin React, sin three.js, sin efectos. Así se puede testear
 * la corrección del acomodo sin montar una escena 3D.
 */

// ── Tipos ────────────────────────────────────────────────────────────────────

export interface LayoutBox {
  /** Identificador del `ContainerItem`. */
  id: string;
  /** Posición de la esquina inferior-izquierda-trasera, en cm. */
  x: number;
  y: number;
  z: number;
  /** Dimensiones YA rotadas: si `rotated`, largo y ancho vienen intercambiados. */
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  rotated: boolean;
  weightKg: number;
}

export interface ContainerBounds {
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxWeightKg: number;
}

export type PlacementProblem =
  | 'out-of-bounds-length'
  | 'out-of-bounds-width'
  | 'out-of-bounds-height'
  | 'collision'
  | 'overweight';

export interface PlacementCheck {
  valid: boolean;
  problems: PlacementProblem[];
  /** Ids de las cajas con las que choca, para poder resaltarlas. */
  collidesWith: string[];
}

export interface LoadStats {
  usedVolumeCbm: number;
  containerVolumeCbm: number;
  fillPercent: number;
  usedWeightKg: number;
  maxWeightKg: number;
  weightPercent: number;
  overweight: boolean;
  /** Reparto del peso entre la mitad delantera y trasera del contenedor (%). */
  frontLoadPercent: number;
  rearLoadPercent: number;
  /** `true` si una mitad carga más del umbral — riesgo de exceso por eje. */
  unbalanced: boolean;
}

/** Paso de la grilla de encastre, en cm. Múltiplos de 5 se sienten naturales. */
export const DEFAULT_GRID_CM = 5;

/**
 * Desbalance a partir del cual se advierte. Con 60/40 ya conviene redistribuir;
 * los límites legales por eje dependen de la ruta y del camión.
 *
 * ⚠ Pendiente de confirmar el umbral real para la ruta Arica→Bolivia.
 */
export const UNBALANCED_THRESHOLD_PERCENT = 60;

/** Tolerancia para comparar centímetros. Evita falsos choques por decimales. */
const EPSILON = 0.001;

// ── Primitivas geométricas ───────────────────────────────────────────────────

/** Redondea al múltiplo de grilla más cercano, sin bajar de 0. */
export function snapToGrid(value: number, gridCm: number = DEFAULT_GRID_CM): number {
  if (gridCm <= 0) return Math.max(0, value);
  return Math.max(0, Math.round(value / gridCm) * gridCm);
}

/**
 * ¿Se solapan dos cajas?
 *
 * Tocarse NO es solaparse: dos cajas pegadas comparten cara y eso es
 * exactamente lo que se busca al acomodar carga.
 */
export function collides(a: LayoutBox, b: LayoutBox): boolean {
  return (
    a.x + a.lengthCm > b.x + EPSILON &&
    b.x + b.lengthCm > a.x + EPSILON &&
    a.y + a.widthCm > b.y + EPSILON &&
    b.y + b.widthCm > a.y + EPSILON &&
    a.z + a.heightCm > b.z + EPSILON &&
    b.z + b.heightCm > a.z + EPSILON
  );
}

/** ¿Las huellas en planta (XY) de dos cajas se pisan? Se usa para la gravedad. */
function footprintsOverlap(a: LayoutBox, b: LayoutBox): boolean {
  return (
    a.x + a.lengthCm > b.x + EPSILON &&
    b.x + b.lengthCm > a.x + EPSILON &&
    a.y + a.widthCm > b.y + EPSILON &&
    b.y + b.widthCm > a.y + EPSILON
  );
}

/** ¿La caja entra dentro de las paredes del contenedor? */
export function withinBounds(box: LayoutBox, bounds: ContainerBounds): boolean {
  return (
    box.x >= -EPSILON &&
    box.y >= -EPSILON &&
    box.z >= -EPSILON &&
    box.x + box.lengthCm <= bounds.lengthCm + EPSILON &&
    box.y + box.widthCm <= bounds.widthCm + EPSILON &&
    box.z + box.heightCm <= bounds.heightCm + EPSILON
  );
}

// ── Gravedad ─────────────────────────────────────────────────────────────────

/**
 * Altura a la que queda apoyada una caja al soltarla — el efecto Tetris.
 *
 * Busca la superficie más alta que quede debajo de su huella. Si no hay nada
 * abajo, cae al piso (z = 0). No valida choques laterales: eso es
 * responsabilidad de `checkPlacement`.
 */
export function findRestingZ(box: LayoutBox, others: LayoutBox[]): number {
  let resting = 0;
  for (const other of others) {
    if (other.id === box.id) continue;
    if (!footprintsOverlap(box, other)) continue;
    const top = other.z + other.heightCm;
    if (top > resting) resting = top;
  }
  return resting;
}

/** Deja caer la caja hasta su punto de apoyo. */
export function dropToRest(box: LayoutBox, others: LayoutBox[]): LayoutBox {
  return { ...box, z: findRestingZ(box, others) };
}

// ── Validación ───────────────────────────────────────────────────────────────

/**
 * Valida una posición candidata.
 *
 * Devuelve todos los problemas encontrados, no solo el primero: la interfaz
 * necesita poder decir "se sale por el largo Y además choca con estas dos".
 */
export function checkPlacement(
  candidate: LayoutBox,
  others: LayoutBox[],
  bounds: ContainerBounds
): PlacementCheck {
  const problems: PlacementProblem[] = [];
  const collidesWith: string[] = [];

  if (candidate.x < -EPSILON || candidate.x + candidate.lengthCm > bounds.lengthCm + EPSILON) {
    problems.push('out-of-bounds-length');
  }
  if (candidate.y < -EPSILON || candidate.y + candidate.widthCm > bounds.widthCm + EPSILON) {
    problems.push('out-of-bounds-width');
  }
  if (candidate.z < -EPSILON || candidate.z + candidate.heightCm > bounds.heightCm + EPSILON) {
    problems.push('out-of-bounds-height');
  }

  for (const other of others) {
    if (other.id === candidate.id) continue;
    if (collides(candidate, other)) collidesWith.push(other.id);
  }
  if (collidesWith.length > 0) problems.push('collision');

  const totalWeight = others
    .filter((o) => o.id !== candidate.id)
    .reduce((sum, o) => sum + o.weightKg, candidate.weightKg);
  if (bounds.maxWeightKg > 0 && totalWeight > bounds.maxWeightKg + EPSILON) {
    problems.push('overweight');
  }

  return { valid: problems.length === 0, problems, collidesWith };
}

/** Mensajes en español para la interfaz. */
export function describeProblem(problem: PlacementProblem): string {
  switch (problem) {
    case 'out-of-bounds-length':
      return 'Se sale por el largo del contenedor';
    case 'out-of-bounds-width':
      return 'Se sale por el ancho del contenedor';
    case 'out-of-bounds-height':
      return 'Supera la altura del contenedor';
    case 'collision':
      return 'Choca con otra carga';
    case 'overweight':
      return 'Supera el peso máximo del contenedor';
  }
}

// ── Transformaciones ─────────────────────────────────────────────────────────

/**
 * Rota la caja 90° sobre el eje vertical: intercambia largo y ancho.
 *
 * No se rota sobre los otros ejes a propósito — volcar la carga rara vez es
 * admisible en un contenedor real (frágil, apilable en una sola orientación).
 */
export function rotateBox(box: LayoutBox): LayoutBox {
  return {
    ...box,
    lengthCm: box.widthCm,
    widthCm: box.lengthCm,
    rotated: !box.rotated,
  };
}

/** Mueve la caja en planta, encastrando a la grilla. */
export function moveBox(
  box: LayoutBox,
  deltaX: number,
  deltaY: number,
  gridCm: number = DEFAULT_GRID_CM
): LayoutBox {
  return {
    ...box,
    x: snapToGrid(box.x + deltaX, gridCm),
    y: snapToGrid(box.y + deltaY, gridCm),
  };
}

/**
 * Acomoda la caja en la posición pedida: encastra, la deja caer y valida.
 *
 * Es la operación que usa el arrastre: devuelve dónde quedaría y si es válido,
 * sin modificar nada. Quien llama decide si aplicar o rechazar.
 */
export function previewPlacement(
  box: LayoutBox,
  targetX: number,
  targetY: number,
  others: LayoutBox[],
  bounds: ContainerBounds,
  gridCm: number = DEFAULT_GRID_CM
): { box: LayoutBox; check: PlacementCheck } {
  const snapped: LayoutBox = {
    ...box,
    x: snapToGrid(targetX, gridCm),
    y: snapToGrid(targetY, gridCm),
  };
  const dropped = dropToRest(snapped, others);
  return { box: dropped, check: checkPlacement(dropped, others, bounds) };
}

/**
 * Reasienta todas las cajas para que ninguna quede flotando.
 *
 * Hace falta después de quitar una caja: lo que estaba encima debe bajar.
 * Procesa de abajo hacia arriba para que los apoyos se resuelvan en orden.
 */
export function settleAll(boxes: LayoutBox[]): LayoutBox[] {
  const sorted = [...boxes].sort((a, b) => a.z - b.z);
  const settled: LayoutBox[] = [];
  for (const box of sorted) {
    settled.push(dropToRest(box, settled));
  }
  return settled;
}

// ── Métricas de carga ────────────────────────────────────────────────────────

/**
 * Ocupación, peso y reparto delantero/trasero.
 *
 * El reparto se calcula por el centro de cada caja respecto de la mitad del
 * largo del contenedor. Es una aproximación: el reparto real por eje depende
 * del chasis del camión, pero alcanza para advertir un desbalance evidente.
 */
export function computeLoadStats(boxes: LayoutBox[], bounds: ContainerBounds): LoadStats {
  const containerVolumeCbm = (bounds.lengthCm * bounds.widthCm * bounds.heightCm) / 1_000_000;

  let usedVolumeCm3 = 0;
  let usedWeightKg = 0;
  let frontWeight = 0;
  let rearWeight = 0;
  const midpoint = bounds.lengthCm / 2;

  for (const box of boxes) {
    usedVolumeCm3 += box.lengthCm * box.widthCm * box.heightCm;
    usedWeightKg += box.weightKg;
    const centerX = box.x + box.lengthCm / 2;
    if (centerX < midpoint) frontWeight += box.weightKg;
    else rearWeight += box.weightKg;
  }

  const usedVolumeCbm = usedVolumeCm3 / 1_000_000;
  const fillPercent = containerVolumeCbm > 0 ? (usedVolumeCbm / containerVolumeCbm) * 100 : 0;
  const weightPercent = bounds.maxWeightKg > 0 ? (usedWeightKg / bounds.maxWeightKg) * 100 : 0;

  const frontLoadPercent = usedWeightKg > 0 ? (frontWeight / usedWeightKg) * 100 : 0;
  const rearLoadPercent = usedWeightKg > 0 ? (rearWeight / usedWeightKg) * 100 : 0;

  const round1 = (n: number) => Math.round(n * 10) / 10;
  const round2 = (n: number) => Math.round(n * 100) / 100;

  return {
    usedVolumeCbm: round2(usedVolumeCbm),
    containerVolumeCbm: round2(containerVolumeCbm),
    fillPercent: round1(fillPercent),
    usedWeightKg: round2(usedWeightKg),
    maxWeightKg: bounds.maxWeightKg,
    weightPercent: round1(weightPercent),
    overweight: bounds.maxWeightKg > 0 && usedWeightKg > bounds.maxWeightKg,
    frontLoadPercent: round1(frontLoadPercent),
    rearLoadPercent: round1(rearLoadPercent),
    unbalanced:
      usedWeightKg > 0 &&
      Math.max(frontLoadPercent, rearLoadPercent) > UNBALANCED_THRESHOLD_PERCENT,
  };
}

// ── Acomodo automático ───────────────────────────────────────────────────────

/**
 * Acomoda todas las cajas de forma automática — el botón "Auto-acomodar".
 *
 * Usa puntos extremos (esquinas libres que dejan las cajas ya colocadas) en vez
 * de barrer toda la grilla: con un 40HQ la grilla son más de 11.000 celdas por
 * caja, y las esquinas son un puñado. El resultado es el mismo porque una caja
 * siempre conviene apoyarla contra otra o contra la pared.
 *
 * Valida con `previewPlacement`, o sea con exactamente las mismas reglas que el
 * acomodo manual. Eso garantiza que nunca produzca un acomodo que después el
 * editor rechace: la gravedad, los choques y el peso se aplican igual.
 *
 * Las que no entran vuelven en `tray` — típicamente significan un segundo
 * contenedor.
 */
export function autoArrange(
  boxes: LayoutBox[],
  bounds: ContainerBounds,
  gridCm: number = DEFAULT_GRID_CM
): { placed: LayoutBox[]; tray: LayoutBox[] } {
  // First Fit Decreasing: las más grandes primero, que es como se carga de verdad.
  const pending = [...boxes].sort(
    (a, b) => b.lengthCm * b.widthCm * b.heightCm - a.lengthCm * a.widthCm * a.heightCm
  );

  const placed: LayoutBox[] = [];
  const tray: LayoutBox[] = [];

  for (const box of pending) {
    // Esquinas candidatas: el origen y las que liberan las cajas ya puestas.
    const candidates: Array<{ x: number; y: number }> = [{ x: 0, y: 0 }];
    for (const p of placed) {
      candidates.push({ x: snapToGrid(p.x + p.lengthCm, gridCm), y: p.y });
      candidates.push({ x: p.x, y: snapToGrid(p.y + p.widthCm, gridCm) });
    }
    // Fondo-izquierda primero: se llena una fila a lo largo y recién ahí la siguiente.
    candidates.sort((a, b) => a.y - b.y || a.x - b.x);

    const orientations = [box, rotateBox(box)];

    let landed: LayoutBox | null = null;
    for (const point of candidates) {
      for (const orientation of orientations) {
        const result = previewPlacement(orientation, point.x, point.y, placed, bounds, gridCm);
        if (result.check.valid) {
          landed = result.box;
          break;
        }
      }
      if (landed) break;
    }

    if (landed) placed.push(landed);
    else tray.push({ ...box, x: 0, y: 0, z: 0 });
  }

  return { placed, tray };
}

// ── Secuencia de carga ───────────────────────────────────────────────────────

/**
 * Orden en que hay que cargar las cajas para que el acomodo sea posible.
 *
 * Va de atrás hacia adelante y de abajo hacia arriba: es como se carga un
 * contenedor de verdad, y es lo que necesita el plan de estiba que se le manda
 * al proveedor.
 */
export function computeLoadingSequence(boxes: LayoutBox[]): LayoutBox[] {
  return [...boxes].sort((a, b) => {
    if (Math.abs(a.z - b.z) > EPSILON) return a.z - b.z; // primero lo de abajo
    if (Math.abs(a.x - b.x) > EPSILON) return b.x - a.x; // luego lo del fondo
    return a.y - b.y;
  });
}
